import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ScrollableTable, Td, Th, Tr } from 'components/tables'
import { DateTime } from 'luxon'
import { Trans, useTranslation } from 'react-i18next'
import { useDateTimeToLocaleString } from '@hooks/useDateTimeWithLocale'
import { ActionDropdown } from './ActionDropdown'
import Status from './Status'
import { useModal } from '@hooks/useModal'
import { CorrectionAction, CorrectionActionFilter } from 'types'
import {
  useTakeCorrectionAction,
  useClearCorrectionAction,
} from '@hooks/useCorrections'
import { toast } from '@lib/toasts'
import { ReactNode } from 'react'
import { faUndo } from '@fortawesome/pro-regular-svg-icons'
import { IconButton } from 'components/buttons'
const columnHelper = createColumnHelper<TimeCardCorrection>()

interface Props {
  corrections: TimeCardCorrection[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  loading: boolean
  onNextPage: () => void
  status: CorrectionActionFilter
  statusFilterComponent: ReactNode
}

export const Table = ({
  corrections,
  hasNextPage,
  isFetchingNextPage,
  loading,
  onNextPage,
  status,
  statusFilterComponent,
}: Props) => {
  const { t } = useTranslation()
  const modal = useModal()
  const dateTimeToLocaleString = useDateTimeToLocaleString()
  const toLocaleDateString = dateTimeToLocaleString(DateTime.DATE_MED)
  const toLocaleDateTimeString = dateTimeToLocaleString(DateTime.DATETIME_FULL)
  const takeCorrectionActionQuery = useTakeCorrectionAction({ status })
  const useClearCorrectionActionQuery = useClearCorrectionAction({ status })

  const takeCorrectionAction = async (
    action: CorrectionAction,
    correctionId: number,
  ) => {
    try {
      await takeCorrectionActionQuery.mutateAsync({
        action,
        correctionId,
      })
      toast({
        title: t('common.success'),
        content:
          action === CorrectionAction.Completed
            ? t('features.correctionsQueue.successfullyMarkedAsCompleted')
            : t('features.correctionsQueue.successfullyMarkedAsNotRequired'),
        variant: 'success',
      })
    } catch (error) {
      toast({
        title: t('common.error'),
        content: t('features.correctionsQueue.somethingWentWrong'),
        variant: 'error',
      })
    }
  }

  const confirmPayrollAction = (
    action: CorrectionAction,
    options: { correctionId: number; employeeName: string; date: DateTime },
  ) => {
    modal.confirm({
      title: t('features.correctionsQueue.confirmAction'),
      content: (
        <Trans
          i18nKey="features.correctionsQueue.pleaseConfirmAction"
          values={{
            employeeName: options.employeeName,
            date: toLocaleDateString(options.date),
            action: t(
              `features.correctionsQueue.confirmationActionText.${action}`,
            ),
          }}
          components={{ s: <strong /> }}
        />
      ),
      onConfirmAsync: () => takeCorrectionAction(action, options.correctionId),
    })
  }

  const clearCorrectionAction = async (correctionId: number) => {
    try {
      await useClearCorrectionActionQuery.mutateAsync({
        correctionId,
      })
      toast({
        title: t('common.success'),
        content: t(
          'features.correctionsQueue.successfullyClearedPayrollAction',
        ),
        variant: 'success',
      })
    } catch (error) {
      toast({
        title: t('common.error'),
        content: t('features.correctionsQueue.somethingWentWrong'),
        variant: 'error',
      })
    }
  }

  const confirmClearPayrollAction = (
    correctionId: number,
    options: { action: CorrectionAction; employeeName: string; date: DateTime },
  ) => {
    modal.confirm({
      title: t('common.confirm'),
      content: (
        <Trans
          i18nKey="features.correctionsQueue.pleaseConfirmClearAction"
          values={{
            employeeName: options.employeeName,
            date: toLocaleDateString(options.date),
            action: t(
              `features.correctionsQueue.payrollActionStatuses.${options.action}`,
            ),
          }}
          components={{ s: <strong /> }}
        />
      ),
      onConfirmAsync: () => clearCorrectionAction(correctionId),
    })
  }

  const columns = [
    columnHelper.accessor('timeCard.worker.fullName', {
      header: t('common.employeeName'),
      cell: ({ getValue }) => <span className="font-bold">{getValue()}</span>,
    }),
    columnHelper.accessor('timeCard.date', {
      header: t('features.correctionsQueue.timeCardDate'),
      cell: ({ getValue }) => toLocaleDateString(getValue()),
    }),
    columnHelper.accessor('timeCard.facility.name', {
      header: t('common.facility'),
    }),
    columnHelper.accessor((row) => row.payrollAction?.action ?? null, {
      id: 'status',
      header: t('common.status'),
      cell: ({ getValue }) => <Status action={getValue()} />,
    }),
    columnHelper.accessor((row) => row.payrollAction?.setAt, {
      header: t('features.correctionsQueue.payrollAction'),
      cell: ({ getValue, row }) => {
        const date = getValue()
        if (date)
          return (
            <span className="text-neutral-500 flex items-center gap-2">
              {t('features.correctionsQueue.statusSetOn', {
                timestamp: toLocaleDateTimeString(date),
              })}

              <IconButton
                icon={faUndo}
                onClick={() => {
                  if (!row.original.payrollAction?.action) return

                  confirmClearPayrollAction(row.original.id, {
                    action: row.original.payrollAction.action,
                    employeeName: row.original.timeCard.worker.fullName,
                    date: row.original.timeCard.date,
                  })
                }}
              />
            </span>
          )

        return (
          <ActionDropdown
            onSelection={(action) =>
              confirmPayrollAction(action, {
                correctionId: row.original.id,
                employeeName: row.original.timeCard.worker.fullName,
                date: row.original.timeCard.date,
              })
            }
          />
        )
      },
    }),
  ]

  const table = useReactTable({
    data: corrections,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <ScrollableTable
      numOfColumns={columns.length}
      body={table.getRowModel().rows.map((row) => {
        return (
          <Tr virtualized={true} estimatedHeight={64} key={row.id}>
            {row.getVisibleCells().map((cell) => {
              return (
                <Td key={cell.id} className="p-4">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              )
            })}
          </Tr>
        )
      })}
      hasNextPage={hasNextPage}
      header={table.getHeaderGroups().map((headerGroup) => (
        <Tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            return (
              <Th
                key={header.id}
                label={flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
                sortDir={header.column.getIsSorted()}
              >
                {header.column.id === 'status' && statusFilterComponent}
              </Th>
            )
          })}
        </Tr>
      ))}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={loading}
      onNextPage={onNextPage}
      extraContent={
        !loading &&
        table.getRowModel().rows.length === 0 && (
          <div className="p-4 text-center text-neutral-500">
            {t('features.correctionsQueue.noCorrections')}
          </div>
        )
      }
    />
  )
}
